import { getUser } from '@frontend/auth-helpers';
import { SchemaIO } from '@frontend/schema';
import { useGetUnifiedThreadEnabled } from '../../utils';
import { useSMSDataV4QueryUpdaters } from '../../v4/query-updaters';
import { useUnifiedThreadUpdater } from '../../v4/query-updaters';
import { SMSMutationContext, useSMSDataV3QueryUpdaters } from '../query-updaters';
import { OptimisticUpdateStatusDetails, ServiceMutations } from '../types';
import { UseSMSDataV3MutationEndpointArgs, useSMSDataV3Mutation } from './use-sms-data-v3-mutation';

type MutationContext<C = unknown> = {
  mutationContext: SMSMutationContext & { didOptimisticUpdate: boolean };
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `DeleteSMS` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `true`.
 */
export const useDeleteSMSMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends {
    threadId: string;
  } = {
    threadId: string;
  },
  RequestOverride extends SchemaIO<ServiceMutations['DeleteSMS']>['input'] = SchemaIO<
    ServiceMutations['DeleteSMS']
  >['input']
>({
  options,
  optimisticUpdate = true,
  ...args
}: UseSMSDataV3MutationEndpointArgs<'DeleteSMS', E, C | undefined, OtherOptions, RequestOverride> & {
  optimisticUpdate?: boolean;
} = {}) => {
  const { deleteSMS, updateSMS } = useSMSDataV3QueryUpdaters();
  const v4QueryUpdaters = useSMSDataV4QueryUpdaters({ httpOptions: args.httpOptions });
  const unifiedThreadUpdater = useUnifiedThreadUpdater({ httpOptions: args.httpOptions });
  const user = getUser();
  const getUnifiedThreadEnabled = useGetUnifiedThreadEnabled();

  return useSMSDataV3Mutation<'DeleteSMS', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'DeleteSMS',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        const mutationContext: MutationContext['mutationContext'] = {
          didOptimisticUpdate: false,
        };

        if (optimisticUpdate) {
          if (getUnifiedThreadEnabled([request.locationId]) && request._otherOptions?.threadId) {
            // Use unified thread updater when flag is enabled
            const result = await unifiedThreadUpdater.deleteThreadItem({
              matchValues: {
                threadItemId: request.smsId,
                threadId: request._otherOptions.threadId,
                locationId: request.locationId,
              },
              statusDetails: OptimisticUpdateStatusDetails.DELETING,
              isOptimistic: true,
            });

            if (result.revertCacheUpdate) {
              mutationContext.didOptimisticUpdate = true;
              mutationContext.revertOptimisticUpdate = result.revertCacheUpdate;
            }
          } else {
            // Use V3 SMS Data when flag is disabled
            const v3Context = deleteSMS({
              matchValues: { id: request.smsId, locationId: request.locationId },
              deletedAt: new Date().toISOString(),
              deletedBy: request.userId || user?.userID || '',
              // Force soft delete so we can revert the changes if the mutation fails
              statusDetails: OptimisticUpdateStatusDetails.DELETING,
              forceSoftDelete: true,
              skipInvalidation: true,
            });

            mutationContext.didOptimisticUpdate = true;
            mutationContext.originalSMS = v3Context.originalSMS;
            mutationContext.updatedSMS = v3Context.updatedSMS;
            mutationContext.revertOptimisticUpdate = () => {
              v3Context.revertOptimisticUpdate?.();
            };
          }
        }

        return {
          mutationContext,
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: async (response, request, context) => {
        if (request._otherOptions?.threadId) {
          if (getUnifiedThreadEnabled([request.locationId])) {
            // Use unified thread updater when flag is enabled
            await unifiedThreadUpdater.deleteThreadItem({
              matchValues: {
                threadItemId: request.smsId,
                threadId: request._otherOptions.threadId,
                locationId: request.locationId,
              },
              statusDetails: '',
              isOptimistic: false,
            });
          } else {
            // Always update V3 cache for backward compatibility
            deleteSMS({
              matchValues: { id: request.smsId, locationId: request.locationId },
              deletedAt: response.deletedAt || new Date().toISOString(),
              deletedBy: response.deletedBy || request.userId || user?.userID || '',
              statusDetails: context?.mutationContext.originalSMS?.statusDetails || '',
            });
            // Use V4 query updaters when flag is disabled
            v4QueryUpdaters.deleteSms({
              matchValues: {
                threadId: request._otherOptions.threadId,
                locationId: request.locationId,
                smsId: request.smsId,
              },
              isOptimistic: false,
            });
          }
        }

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        if (context?.mutationContext.didOptimisticUpdate) {
          // Revert the optimistic update
          updateSMS({
            matchValues: { id: request.smsId, locationId: request.locationId },
            newValues: {
              deletedAt: context.mutationContext.originalSMS?.deletedAt || '',
              deletedBy: context.mutationContext.originalSMS?.deletedBy || '',
              statusDetails: context.mutationContext.originalSMS?.statusDetails || '',
              tags: context.mutationContext.originalSMS?.tags || [],
              tagsDetailed: context.mutationContext.originalSMS?.tagsDetailed || [],
              tasks: context.mutationContext.originalSMS?.tasks || [],
            },
            skipInvalidation: true,
          });
          context?.mutationContext.revertOptimisticUpdate?.();
        }

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
